import React, { ReactElement, useEffect, useState } from 'react'
import { Box, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import HeaderMenu from './HeaderMenu'
import SidebarMenu from './SidebarMenu'
import ShellThemeProvider from './ShellThemeProvider'
import { SIDEBAR_STORAGE_KEY } from './layoutConstants'

interface Props {
  children: ReactElement
}

const getStoredSidebarOpen = (): boolean => {
  try {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY)
    if (stored === null) return true
    return stored === 'true'
  } catch {
    return true
  }
}

const setStoredSidebarOpen = (open: boolean) => {
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(open))
  } catch {
    // ignore
  }
}

/** Shell giống hẳn ytl-logistics: Header + (Sidebar | Content), MUI. */
const BasicLayoutInner: React.FC<Props> = ({ children }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [sidebarOpen, setSidebarOpen] = useState(getStoredSidebarOpen)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)

  useEffect(() => {
    if (!isMobile) setMobileDrawerOpen(false)
  }, [isMobile])

  const handleToggleSidebar = () => {
    if (isMobile) {
      setMobileDrawerOpen((prev) => !prev)
      return
    }
    setSidebarOpen((prev) => {
      const next = !prev
      setStoredSidebarOpen(next)
      return next
    })
  }

  return (
    <Box
      component="main"
      sx={{
        width: '100%',
        maxWidth: '100vw',
        height: '100vh',
        minHeight: '-webkit-fill-available',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
        backgroundColor: 'background.default',
      }}
    >
      <HeaderMenu
        sidebarOpen={isMobile ? mobileDrawerOpen : sidebarOpen}
        onToggle={handleToggleSidebar}
      />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          maxWidth: '100vw',
          display: 'flex',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <SidebarMenu
          openSidebar={sidebarOpen}
          isMobile={isMobile}
          mobileOpen={mobileDrawerOpen}
          onMobileClose={() => setMobileDrawerOpen(false)}
        />
        <Box
          component="div"
          className="nhansu-shell-content"
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            padding: { xs: 0.5, sm: 0.75, md: 1 },
            overflow: 'auto',
            backgroundColor: 'background.default',
            WebkitOverflowScrolling: 'touch',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}

const BasicLayout: React.FC<Props> = ({ children }) => (
  <ShellThemeProvider>
    <BasicLayoutInner>{children}</BasicLayoutInner>
  </ShellThemeProvider>
)

export default BasicLayout
